import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const HealthLanding = () => {
  const { ref: introRef, isVisible: introVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: philRef, isVisible: philVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <>
      <Helmet>
        <title>Health | Prashant Sudeep</title>
        <meta name="description" content="A personal knowledge base on health, nutrition, fitness, and long-term wellbeing — backed by research and cited sources." />
        <meta property="og:title" content="Health Knowledge Base | Prashant Sudeep" />
        <meta property="og:description" content="Long-term health principles, nutrition by age, sugar deep dive, and a searchable food & nutrient index." />
      </Helmet>

      <div className="health-page">
        {/* Hero / Why */}
        <motion.div
          ref={introRef}
          initial={{ opacity: 0, y: 24 }}
          animate={introVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.5 }}
          className="health-content"
        >
          <h1 className="health-page-title">Health</h1>

          <p className="text-gray-300" style={{ fontSize: '1.0625rem', lineHeight: 1.75, marginBottom: '1rem' }}>
            Health is a compounding decision. The food you eat, how you move, how you sleep, how you
            manage stress — these aren't one-off choices. They accumulate quietly over years and decades,
            and the gap between people who get the basics right and those who don't widens dramatically
            after 40.
          </p>
          <p className="text-gray-300" style={{ fontSize: '1.0625rem', lineHeight: 1.75, marginBottom: '1rem' }}>
            This section is my personal synthesis of what I've read, tested, and found worth keeping.
            It's not a fitness blog or a diet plan — it's a reference I maintain for myself and my
            family, organised so you can find what you need quickly and trust the citations behind it.
          </p>
          <p className="text-gray-300" style={{ fontSize: '1.0625rem', lineHeight: 1.75, marginBottom: '2rem' }}>
            Everything here is backed by research where possible, flagged where evidence is mixed,
            and accompanied by a disclaimer — because I'm not a doctor, and neither is this page.
          </p>
        </motion.div>

        {/* How to use — sub-section cards */}
        <motion.div
          ref={cardsRef}
          initial={{ opacity: 0, y: 24 }}
          animate={cardsVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '1rem', fontFamily: '"DM Sans", sans-serif' }}>
            How to use this section
          </h2>

          <div className="health-intro-cards">
            <Link to="/health/principles" className="health-intro-card">
              <div className="health-intro-card-label">Evergreen</div>
              <div className="health-intro-card-title">Principles</div>
              <div className="health-intro-card-desc">
                The fundamentals — nutrition, movement, sleep, and anti-aging — that hold true regardless of age or trend.
              </div>
            </Link>

            <Link to="/health/by-age" className="health-intro-card">
              <div className="health-intro-card-label">Life stages</div>
              <div className="health-intro-card-title">By Age</div>
              <div className="health-intro-card-desc">
                Priorities broken down by age bracket, from infancy to 60+. What matters most at each stage.
              </div>
            </Link>

            <Link to="/health/food" className="health-intro-card">
              <div className="health-intro-card-label">Search</div>
              <div className="health-intro-card-title">Food & Nutrients</div>
              <div className="health-intro-card-desc">
                A searchable index of nutrients, foods, and health topics — protein, vitamins, minerals, and more.
              </div>
            </Link>
          </div>

          <div style={{ marginTop: '0.5rem' }}>
            <Link
              to="/health/sugar"
              className="health-intro-card"
              style={{ maxWidth: '100%', borderLeftColor: 'var(--color-gold)', borderLeftWidth: 3, borderLeftStyle: 'solid' }}
            >
              <div className="health-intro-card-label" style={{ color: 'var(--color-gold)' }}>Deep Dive</div>
              <div className="health-intro-card-title">Sugar: The Full Picture</div>
              <div className="health-intro-card-desc">
                All forms of sugar, what it does to every system in your body, brain and mood effects, and practical steps to reduce intake.
              </div>
            </Link>
          </div>
        </motion.div>

        <hr className="health-divider" />

        {/* Core philosophy */}
        <motion.div
          ref={philRef}
          initial={{ opacity: 0, y: 24 }}
          animate={philVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.5 }}
        >
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '1.25rem', fontFamily: '"DM Sans", sans-serif' }}>
            Core philosophy
          </h2>

          <ul className="health-philosophy-list">
            {PHILOSOPHY_ITEMS.map((item, i) => (
              <li key={i} className="health-philosophy-item">
                <span className="health-philosophy-bullet" />
                <span className="health-philosophy-text">
                  {item.text}
                  {item.link && (
                    <>
                      {' '}<Link to={item.link} style={{ color: 'var(--color-accent)', textDecoration: 'none', fontSize: '0.8125rem' }}>Read more &rarr;</Link>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </>
  );
};

const PHILOSOPHY_ITEMS = [
  {
    text: 'Minimise added sugar — it compounds metabolic damage across every system in the body.',
    link: '/health/sugar',
  },
  {
    text: 'Prioritise protein and fibre at every meal — they control hunger, stabilise blood sugar, and support muscle.',
    link: '/health/food/protein',
  },
  {
    text: 'Resistance train regardless of age — it\'s the single most effective anti-aging intervention you can do.',
    link: '/health/principles',
  },
  {
    text: 'Sleep is a pillar, not a luxury — chronic short sleep undermines everything else you do for your health.',
    link: '/health/principles',
  },
  {
    text: 'Consistency over intensity — small daily habits beat dramatic monthly efforts, every time.',
    link: null,
  },
  {
    text: 'Whole foods over supplements — food quality is the foundation; supplements fill specific gaps, they don\'t replace diet.',
    link: '/health/principles',
  },
  {
    text: 'Move every day, not just at the gym — walking, stairs, standing, and daily activity matter as much as workouts.',
    link: '/health/principles',
  },
  {
    text: 'Start where you are — the best time to build these habits was 10 years ago; the second best time is now.',
    link: null,
  },
];

export default HealthLanding;
